export type ChannelId =
  | 'slack'
  | 'msteams'
  | 'telegram'
  | 'whatsapp'
  | 'signal'
  | 'discord'
  | 'irc'
  | 'matrix'
  | 'mattermost'
  | 'imessage'
  | 'google-chat'
  | 'nostr'
  | 'line'
  | 'feishu'
  | 'nextcloud-talk'
  | 'zalo'
  | 'tlon'
  | 'bluebubbles'
  | 'beam'
  | 'direct-chat'
  | 'grok-voice';

export type ChannelSecurityTier = 'elevated' | 'standard' | 'limited';

/**
 * Who is allowed to send messages to an agent on a given channel.
 *
 * Runtime source of truth for the mode set (ENG-7757). The API write
 * validators (agents.ts sender_policy, organizations.ts channel-policy) import
 * this constant instead of copying the literals - the pre-ENG-7757 copies
 * drifted to 3 modes and rejected team_only/manager_only writes. The DB CHECKs
 * (agents_sender_policy_chk, organization_channel_policies_sender_policy_chk,
 * last widened by migration 20260602000003) must accept exactly this list.
 */
export const SENDER_POLICY_MODES = [
  'all',
  'agents_only',
  'team_only',
  'team_agents_only',
  'manager_only',
] as const;

export type SenderPolicyMode = (typeof SENDER_POLICY_MODES)[number];

/**
 * Channels where `sender_policy` is actually ENFORCED — i.e. where an MCP
 * channel server reads the resolved principals and drops inbound that fails
 * the mode check.
 *
 * THE SAME SHAPE AS THE MODE LIST ABOVE, ONE FIELD OVER. ENG-7757 moved
 * `SENDER_POLICY_MODES` here because copies of the literals had drifted to
 * three modes and were rejecting valid writes. The enforced-channel set had
 * not been consolidated, and by ENG-9662 there were FOUR copies of it:
 *
 *   packages/webapp/src/components/channels/sender-policy.tsx  (exported)
 *   apps/cli/src/lib/manager-worker.ts   (twice, as inline literals)
 *   packages/api/src/routes/host-runtime.ts   (absent — see below)
 *
 * The webapp's own header warns that "a second copy of the enforced set is the
 * same drift risk one field over". It was right, and three more copies already
 * existed outside its view.
 *
 * THE DRIFT IS WORSE HERE THAN IT WAS FOR THE MODES, because it fails quietly
 * in the opposite direction. A stale mode list REJECTED a write, which is
 * loud. A missing enforced-channel check makes the API resolve team members'
 * Telegram chat ids — personal identifiers out of `contact_preferences` — and
 * ship them in every `/host/refresh` body, roughly every ten seconds, to every
 * polling host, for a consumer that does not exist. Nothing reads them:
 * `TELEGRAM_SENDER_POLICY` appears nowhere in the tree, and
 * `channels/governance/telegram-sender-gate.ts` says so in its header.
 *
 * ADD A CHANNEL HERE ONLY WHEN ITS MCP SERVER ACTUALLY FILTERS ON THE POLICY,
 * never when it merely receives it. Presence in this set is what causes
 * personal identifiers to be resolved and transmitted, so an aspirational
 * entry is an egress, not a to-do. ENG-5836 plans direct-chat / Telegram
 * coverage; when it lands, the entry goes in with the filter, not before it.
 */
export const SENDER_POLICY_ENFORCED_CHANNELS = ['slack', 'msteams'] as const;

export type SenderPolicyEnforcedChannel = (typeof SENDER_POLICY_ENFORCED_CHANNELS)[number];

/** Whether `sender_policy` is enforced on `channelId`. Case-sensitive: channel ids are lowercase everywhere. */
export function senderPolicyIsEnforcedOn(channelId: string): boolean {
  return (SENDER_POLICY_ENFORCED_CHANNELS as readonly string[]).includes(channelId);
}

/**
 * Discriminated union so `team_id` is statically required exactly when the
 * mode actually uses it (`team_agents_only`). This prevents constructing
 * an unenforceable `{ mode: 'team_agents_only' }` with no team scope.
 *
 * `manager_only` (ENG-5842) carries no additional fields here — the
 * principal's slack_user_id / telegram_chat_id / Teams AAD id are resolved
 * at provision time from `agents.reports_to_person_id` and injected into the
 * MCP server as `<CHANNEL>_SENDER_POLICY_PRINCIPAL_ID` env vars.
 *
 * `team_only` (ENG-5871) admits BOTH humans on the same team AND same-team
 * Augmented agents — the friendlier sibling of `team_agents_only` (which
 * drops humans). Carries no fields here — like `manager_only`, the team
 * members' resolved slack_user_id / telegram_chat_id / Teams AAD ids are
 * resolved at provision time (via `team_members` ⋈ `organization_people`
 * (on user_id, NOT NULL) ⋈ `contact_preferences`) and injected as
 * comma-separated `<CHANNEL>_SENDER_POLICY_TEAM_PRINCIPAL_IDS` env vars.
 * Manually-curated organization_people rows with user_id IS NULL are
 * invisible to team_only — they have no auth user and so no resolved
 * channel identity. See migration 20260602000003 header.
 *
 * `internal_only` (ENG-5843) is an orthogonal optional boolean on every
 * variant — when true, the MCP filter additionally drops inbound from
 * external Slack Connect workspaces / Teams external tenants (matched on
 * the sender's workspace/tenant id against `SLACK_HOME_TEAM_ID` /
 * `MSTEAMS_HOME_TENANT_ID` env vars injected by the provisioner). Composes
 * with the mode check: mode admits first, then internal_only narrows. So
 * `{ mode: 'agents_only', internal_only: true }` means "only same-workspace
 * Augmented agents."
 */
export type SenderPolicy =
  | { mode: 'all'; internal_only?: boolean }
  | { mode: 'agents_only'; internal_only?: boolean }
  | { mode: 'team_only'; internal_only?: boolean }
  | {
      mode: 'team_agents_only';
      /**
       * Populated at provisioning time from the org context. Not set in the
       * charter directly — the provisioner injects the team slug so the MCP
       * server can verify incoming labels without a live API call.
       */
      team_id: string;
      internal_only?: boolean;
    }
  | { mode: 'manager_only'; internal_only?: boolean };

export interface ChannelDefinition {
  id: ChannelId;
  name: string;
  securityTier: ChannelSecurityTier;
  e2eEncrypted: boolean | 'optional';
  auditTrail: boolean | 'partial';
  publicExposureRisk: 'Low' | 'Medium' | 'High';
}

export interface ChannelPolicy {
  policy: 'allowlist' | 'denylist';
  allowed: ChannelId[];
  denied: ChannelId[];
  require_approval_to_change: boolean;
}

export interface OrgChannelPolicy {
  organization_id: string;
  allowed_channels: ChannelId[];
  denied_channels: ChannelId[];
  require_elevated_for_pii: boolean;
  /** null / absent = 'all' (no sender restriction). */
  sender_policy?: SenderPolicy;
}

/** @deprecated Use OrgChannelPolicy */
export type TeamChannelPolicy = OrgChannelPolicy;
